import { useEffect, useState } from "react";
import useDebounce from "./useDebounce";
import { useUser } from "../contexts/UserProvider";
import { API_BASE_URL } from "../config";
import { buildQueryString } from "../utils/utils";

const RESULT_LIMIT = 5;
const EMPTY_RESULTS = { clients: [], groups: [], agents: [] };

async function fetchPaginatedResponse(url) {
  const response = await fetch(url);

  if (!response.ok) {
    throw new Error(`Search request failed with status ${response.status}`);
  }

  const result = await response.json();
  return result?.data?.paginatedResponse || [];
}

function dedupeAndLimit(items, idKey) {
  const seen = new Set();
  const deduped = [];

  for (const item of items) {
    if (item == null || seen.has(item[idKey])) {
      continue;
    }

    seen.add(item[idKey]);
    deduped.push(item);

    if (deduped.length === RESULT_LIMIT) {
      break;
    }
  }

  return deduped;
}

// Fans out to the existing per-entity paginated endpoints since firstName/lastName
// filters are applied as separate ANDed conditions server-side - a single search
// term has to be queried against each name field independently, then merged here.
function useGlobalSearch(term) {
  const { id, roles } = useUser();
  const debouncedTerm = useDebounce(term, 300);
  const [results, setResults] = useState(EMPTY_RESULTS);
  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);

  const isAdmin = roles?.includes("ADMIN");
  const isSalesManager = roles?.includes("SALES_MANAGER");
  const isGroupManager = roles?.includes("GROUP_MANAGER");
  const isGroupAdmin = roles?.includes("GROUP_ADMIN");

  useEffect(() => {
    const trimmedTerm = debouncedTerm?.trim();

    if (!id || !trimmedTerm) {
      setResults(EMPTY_RESULTS);
      setIsLoading(false);
      setIsError(false);
      return;
    }

    let cancelled = false;
    setIsLoading(true);
    setIsError(false);

    const pageParams = { page: 0, size: RESULT_LIMIT, order: "ASC" };

    // Group Manager and Group Admin cannot see other agents' individual policies,
    // so unlike groups/agents below, they fall through to the own-policies-only URL.
    const clientBaseUrl = isAdmin
      ? `${API_BASE_URL}/policyholders/admin/all/${id}/v1`
      : isSalesManager
      ? `${API_BASE_URL}/policyholders/get-subordinate-clients/${id}/v1`
      : `${API_BASE_URL}/policyholders/${id}/policies`;

    // Group Admin is scoped like Group Manager here, not to all groups/agents.
    const groupBaseUrl = isAdmin
      ? `${API_BASE_URL}/groups/admin/all/${id}/v1`
      : isGroupManager || isGroupAdmin
      ? `${API_BASE_URL}/groups/get-subordinate-groups/${id}/v1`
      : `${API_BASE_URL}/groups/${id}/policies`;

    const agentBaseUrl = isAdmin
      ? `${API_BASE_URL}/agents/admin/all/${id}/v1`
      : isSalesManager
      ? `${API_BASE_URL}/agents/get-subordinate-agents/${id}/v1`
      : isGroupManager || isGroupAdmin
      ? `${API_BASE_URL}/agents/get-subordinate-agents-by-group-manager/${id}/v1`
      : null;

    // "John Smith": the name filters are ANDed server-side, so a full name is
    // also tried as first word + remainder. Listed first so it ranks first.
    const [firstWord, ...restWords] = trimmedTerm.split(/\s+/);
    const fullNameParams = restWords.length
      ? { firstName: firstWord, lastName: restWords.join(" ") }
      : null;

    const clientRequests = Promise.all([
      ...(fullNameParams
        ? [fetchPaginatedResponse(`${clientBaseUrl}?${buildQueryString({ ...pageParams, ...fullNameParams })}`)]
        : []),
      fetchPaginatedResponse(`${clientBaseUrl}?${buildQueryString({ ...pageParams, firstName: trimmedTerm })}`),
      fetchPaginatedResponse(`${clientBaseUrl}?${buildQueryString({ ...pageParams, lastName: trimmedTerm })}`),
    ]);

    const groupRequests = Promise.all([
      fetchPaginatedResponse(`${groupBaseUrl}?${buildQueryString({ ...pageParams, name: trimmedTerm })}`),
    ]);

    const agentRequests = agentBaseUrl
      ? Promise.all([
          ...(fullNameParams
            ? [fetchPaginatedResponse(`${agentBaseUrl}?${buildQueryString({ ...pageParams, ...fullNameParams })}`)]
            : []),
          fetchPaginatedResponse(`${agentBaseUrl}?${buildQueryString({ ...pageParams, firstName: trimmedTerm })}`),
          fetchPaginatedResponse(`${agentBaseUrl}?${buildQueryString({ ...pageParams, lastName: trimmedTerm })}`),
        ])
      : Promise.resolve([]);

    Promise.all([clientRequests, groupRequests, agentRequests]).then(
      ([clientResponses, groupResponses, agentResponses]) => {
        if (cancelled) {
          return;
        }

        const clients = dedupeAndLimit(
          clientResponses.flat().map((entry) => entry.policyHolder),
          "policyId"
        );

        const groups = dedupeAndLimit(
          groupResponses.flat().map((entry) => entry.group),
          "groupId"
        );

        const agents = dedupeAndLimit(agentResponses.flat(), "id");

        setResults({ clients, groups, agents });
        setIsLoading(false);
      }
    ).catch(() => {
      if (cancelled) {
        return;
      }

      setResults(EMPTY_RESULTS);
      setIsError(true);
      setIsLoading(false);
    });

    return () => {
      cancelled = true;
    };
  }, [debouncedTerm, id, isAdmin, isSalesManager, isGroupManager, isGroupAdmin]);

  return { ...results, isLoading, isError };
}

export default useGlobalSearch;
